import { DateTime } from "luxon";

import { OmnipodSource } from "@/hooks/useOmnipodSource";
import { DeviceDriver, OnData, OnError, OnRawData, RawData } from "@/lib/drivers/DeviceDriver";
import { IBF_LOG_HEADER } from "@/lib/drivers/insulet/IBFRecords/IBF_LOG_HEADER";
import { IBF_LOG_RECORDS } from "@/lib/drivers/insulet/IBFRecords/IBF_LOG_RECORD";
import {
  IBF_MANUFACTURING_NUMBER,
} from "@/lib/drivers/insulet/IBFRecords/IBF_MANUFACTURING_NUMBER";
import { IBF_PDM_VERSION } from "@/lib/drivers/insulet/IBFRecords/IBF_PDM_VERSION";
import {
  IBF_PUMP_PROFILE_LIST,
  IBFPumpProfileList,
} from "@/lib/drivers/insulet/IBFRecords/IBF_PUMP_PROFILE_LIST";
import {
  IBF_PUMP_PROFILE_SETTINGS,
  IBFPumpProfileSettings,
} from "@/lib/drivers/insulet/IBFRecords/IBF_PUMP_PROFILE_SETTINGS";
import { IBF_PUMP_SETTINGS } from "@/lib/drivers/insulet/IBFRecords/IBF_PUMP_SETTINGS";
import { IBF_VERSION } from "@/lib/drivers/insulet/IBFRecords/IBF_VERSION";
import { IBFRecord } from "@/lib/drivers/insulet/IBFRecords/IBFRecord";
import { readIBFRecord } from "@/lib/drivers/insulet/IBFRecords/IBFRecordReader";
import {
  batchRecordParser,
  IBFRecordParsingInfo,
  recordParser,
} from "@/lib/drivers/insulet/IBFRecords/RecordParser";
import { OmnipodConstants } from "@/lib/drivers/insulet/OmnipodConstants.ts";
import {
  OmnipodConfigInfo,
  OmnipodData,
  OmnipodProfile,
  omnipodUploadInfoSchema,
} from "@/lib/drivers/insulet/OmnipodTypes";
import {
  OmnipodBasalRateChangeTransformer,
} from "@/lib/drivers/insulet/transformers/OmnipodBasalRateChangeTransformer";
import {
  OmnipodBolusInsulinTransformer,
} from "@/lib/drivers/insulet/transformers/OmnipodBolusInsulinTransformer";
import {
  OmnipodDeviceTransformer,
} from "@/lib/drivers/insulet/transformers/OmnipodDeviceTransformer";
import {
  OmnipodPumpEventsTransformer,
} from "@/lib/drivers/insulet/transformers/OmnipodPumpEventsTransformer";
import { MediaTransferProtocol } from "@/lib/MediaTransferProtocol";
import { DeviceDataToUpload } from "@/models/DeviceDataToUpload";
import { MedicalDevice } from "@/models/DeviceModel";
import { toDateTime } from "@/utils/dateUtils.ts";
import { Sentry } from "@/utils/sentryUtils";

export class OmnipodDriver extends DeviceDriver<OmnipodConfigInfo, OmnipodData> {
  name = "Omnipod";
  fileContent: ArrayBuffer | undefined;
  _ibfRecords: IBFRecord[] = [];

  constructor(
    private readonly source: OmnipodSource,
    private readonly omnipodDevices: MedicalDevice[],
  ) {
    super();
  }

  _get_file_from_usb = async () => {
    try {
      if (this.source?.type !== "usb") {
        return false;
      }
      const mtp = new MediaTransferProtocol(this.source.device);

      try {
        await mtp.connect();
        await mtp.openSession();
        const handles = await mtp.getObjectHandles();
        const filesInfo = await mtp.getFileInfo(handles);
        const fileInfo = Object.values(filesInfo).find(file => file.name.endsWith(".ibf"));
        if (!fileInfo) {
          return false;
        }
        this.fileContent = await mtp.getFile(fileInfo.handle);

        return true;
      } catch (e) {
        Sentry.captureException(e);
        console.warn(e);
        return false;
      } finally {
        try {
          await mtp.closeSession();
        } catch (e) {
          Sentry.captureException(e);
          console.warn(e);
        }
        try {
          await mtp.disconnect();
        } catch (e) {
          Sentry.captureException(e);
          console.warn(e);
        }
      }
    } catch (e) {
      Sentry.captureException(e);
      console.warn(e);
      return false;
    }
  };

  connect = async (): Promise<boolean> => {
    Sentry.addBreadcrumb({
      type: "default",
      category: "omnipod",
      message: "Starting to connect to Omnipod",
      timestamp: DateTime.now().toMillis(),
      data: {
        sourceType: this.source?.type,
      },
    });
    if (this.source?.type === "manual") {
      this.fileContent = await this.source.file.arrayBuffer();
      return true;
    }
    const usb = await this._get_file_from_usb();
    if (usb) {
      Sentry.addBreadcrumb({
        type: "default",
        category: "omnipod",
        message: "Omnipod connected with usb",
        timestamp: DateTime.now().toMillis(),
      });
      return true;
    }
    return false;
  };

  _parseIbfRecords = () => {
    this._ibfRecords = [];
    if (!this.fileContent) {
      return;
    }
    let buffer = new DataView<ArrayBuffer>(this.fileContent);

    while (buffer.byteLength > 0) {
      const result = readIBFRecord(buffer);
      buffer = result[1];
      if (result[0]) {
        this._ibfRecords.push(result[0]);
      }
    }
  };

  disconnect = async (): Promise<boolean> => {
    this.fileContent = undefined;
    this._ibfRecords = [];
    this.initProgress();
    return true;
  };

  _mergeProfileAndProfileSettings = (
    profilesSettings: IBFPumpProfileSettings,
    profiles: IBFPumpProfileList,
  ): OmnipodProfile[] => {
    return profiles.map(profile => {
      const profileSettings = profilesSettings.profiles.find(p => p.index === profile.index);
      return {
        name: profileSettings?.name ?? "",
        index: profile.index,
        isActive: profileSettings?.isActive ?? false,
        segments: profile.steps,
      };
    });
  };

  retrieveInfo = async (): Promise<OmnipodConfigInfo> => {
    const parserInfo: IBFRecordParsingInfo = {
      isOmnipodDash: false,
      lastFetchedTimestamp: 0,
    };
    const version = recordParser(this._ibfRecords, IBF_VERSION, parserInfo);
    const pdmVersion = recordParser(this._ibfRecords, IBF_PDM_VERSION, parserInfo);
    parserInfo.isOmnipodDash = pdmVersion.data.model === "Dash";
    const serial = recordParser(this._ibfRecords, IBF_MANUFACTURING_NUMBER, parserInfo);
    const profilesSettings = recordParser(this._ibfRecords, IBF_PUMP_PROFILE_SETTINGS, parserInfo);
    const pumpSettings = recordParser(this._ibfRecords, IBF_PUMP_SETTINGS, parserInfo);
    const profilesList = batchRecordParser(this._ibfRecords, IBF_PUMP_PROFILE_LIST, parserInfo);

    const profiles = this._mergeProfileAndProfileSettings(profilesSettings.data, profilesList.data);

    return {
      versions: {
        ibf: version.data.ibfVersion,
        engine: version.data.engineVersion,
        pdm: pdmVersion.data.pdmVersion,
      },
      vendorId: version.data.vendorId,
      productId: version.data.productId,
      model: pdmVersion.data.model,
      manufacturingNumber: serial.data.manufacturingNumber,
      serialNumber: pumpSettings.data.serialNumber,
      profiles,
      pumpSettings: {
        maxBolus: pumpSettings.data.bolusMax,
        maxBasal: pumpSettings.data.basalMax,
        bolusIncrement: pumpSettings.data.bolusIncr,
        hypoglycemiaThreshold: pumpSettings.data.bgGoalLow,
        hyperglycemiaThreshold: pumpSettings.data.bgGoalUp,
        activeInsulinTime: pumpSettings.data.insulinDuration,
        units: pumpSettings.data.units,
      },
    };
  };

  retrieveEvents = async (info: OmnipodConfigInfo): Promise<OmnipodData> => {
    const device = this.omnipodDevices.find(device => device.serialNumber === info.serialNumber);
    let lastFetchedDate: number = 0;
    if (device) {
      const omnipodUploadInfo = omnipodUploadInfoSchema.safeParse(device.lastUploadInfo);

      if (omnipodUploadInfo.success) {
        lastFetchedDate = omnipodUploadInfo.data.last_fetched_date
          ? DateTime.fromISO(omnipodUploadInfo.data.last_fetched_date).toMillis()
          : 0;
      }
    }

    const parserInfo: IBFRecordParsingInfo = {
      isOmnipodDash: info.model === "Dash",
      lastFetchedTimestamp: lastFetchedDate,
    };

    const logsHeader = recordParser(this._ibfRecords, IBF_LOG_HEADER, parserInfo);
    console.info(
      `Retrieving logs from ${lastFetchedDate ? toDateTime(lastFetchedDate).toLocaleString(DateTime.DATETIME_SHORT) : "`forever`"}`,
    );

    const logs = batchRecordParser(
      this._ibfRecords,
      IBF_LOG_RECORDS,
      parserInfo,
      record => record.data.date.toMillis() > lastFetchedDate,
    );

    console.info(
      `Retrieved logs from ${lastFetchedDate ? toDateTime(lastFetchedDate).toLocaleString(DateTime.DATETIME_SHORT) : "`forever`"} to ${logs.data.end?.toLocaleString(DateTime.DATETIME_SHORT)} \nTotal logs: ${logs.data.logs.length} \nIgnored logs: ${logs.data.ignored}`,
    );

    return {
      info,
      uploadDate: logsHeader.data.date,
      logs: logs.data.logs,
      endDate: logs.data.end,
    };
  };

  buildImportRawInfo = (info: OmnipodConfigInfo): RawData | undefined => {
    return {
      raw: this.fileContent ? new Uint8Array(this.fileContent).toString() : "",
      device: {
        display_name: info.model === "Eros" ? OmnipodConstants.EROS_DISPLAY_NAME : OmnipodConstants.DASH_DISPLAY_NAME,
        serial_number: info.serialNumber,
        device_type_name: info.model === "Dash" ? "omnipod dash" : "omnipod",
      },
    };
  };

  transformForUpload = (data: OmnipodData): DeviceDataToUpload => {
    const bolus = new OmnipodBolusInsulinTransformer().transform(data);
    const devices = new OmnipodDeviceTransformer().transform(data);
    const pumpEvents = new OmnipodPumpEventsTransformer().transform(data);
    const basal = new OmnipodBasalRateChangeTransformer().transform(data);

    return {
      ...devices,
      pumpEvents,
      bolus,
      basal,
      glycemia: [],
    };
  };

  override fetchAndUpload = async (onData: OnData, onError: OnError, onRawData: OnRawData) => {
    this.setProgressWhenDone(10);
    this.setProgress(1);
    const connected = await this.connect();
    this.setProgress(2);
    if (connected) {
      try {
        this._parseIbfRecords();
        this.setProgress(3);
        const info = await this.retrieveInfo();
        this.setProgress(4);
        const raw = this.buildImportRawInfo(info);
        this.setProgress(5);
        if (raw) {
          onRawData(raw);
        }
        this.setProgress(6);

        const data = await this.retrieveEvents(info);
        this.setProgress(7);
        const dataDTO = this.transformForUpload(data);
        this.setProgress(8);
        onData(dataDTO);
        this.setProgress(9);
      } catch (e) {
        Sentry.captureException(e);
        console.error(e);
        onError("devices.errors.dataRead");
      } finally {
        await this.disconnect();
      }
    } else {
      onError("devices.errors.connectionFailure");
    }
  };
}
